import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PickListEntry,
  useUpdateCandidateFunnelStageMutation,
} from '../../../../__generated__/graphql';
import { useToasts } from '../../../../../../context/Toast';
import { ConfirmModalSimple } from '../../../../../../components/ConfirmModalSimple/ConfirmModalSimple';
import './FunnelStagePath.css';

type Props = {
  candidateId: string;
  currentStage: string | null | undefined;
  stages: ReadonlyArray<PickListEntry>;
};

const FunnelStagePath: React.FC<Props> = ({
  candidateId,
  currentStage,
  stages,
}) => {
  const { t } = useTranslation();
  const { addError } = useToasts();
  const [optimisticStage, setOptimisticStage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [pendingStage, setPendingStage] = useState<PickListEntry | null>(null);
  const [updateFunnelStage] = useUpdateCandidateFunnelStageMutation();

  const activeStage = optimisticStage ?? currentStage;
  const activeIndex = stages.findIndex((s) => s.value === activeStage);

  const handleStageClick = (stage: PickListEntry) => {
    if (stage.value === activeStage || loading) {
      return;
    }
    setPendingStage(stage);
  };

  const handleConfirm = async () => {
    if (!pendingStage) return;

    const stage = pendingStage;
    setPendingStage(null);
    setOptimisticStage(stage.value);
    setLoading(true);

    try {
      await updateFunnelStage({
        variables: {
          candidateId,
          funnelStage: stage.value,
        },
        update: (cache) => {
          cache.modify({
            id: cache.identify({
              __typename: 'JobCandidate',
              Id: candidateId,
            }),
            fields: {
              flair__Funnel_Stage__c: () => stage.value,
            },
          });
        },
      });
      setOptimisticStage(null);
    } catch {
      setOptimisticStage(null);
      addError(t('recruitment.candidate.detailsPage.funnelStagePath.error'));
    } finally {
      setLoading(false);
    }
  };

  if (stages.length === 0) {
    return null;
  }

  return (
    <>
      <div className="funnel-stage-path mb-3">
        {stages.map((stage, index) => {
          const isActive = stage.value === activeStage;
          const isCompleted = activeIndex >= 0 && index < activeIndex;

          const className = [
            'funnel-stage-step',
            isActive && 'funnel-stage-step--active',
            isCompleted && 'funnel-stage-step--completed',
            loading && 'funnel-stage-step--loading',
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <button
              key={stage.value}
              className={className}
              onClick={() => handleStageClick(stage)}
              title={stage.label}>
              {stage.label}
            </button>
          );
        })}
      </div>
      <ConfirmModalSimple
        visible={pendingStage !== null}
        message={t(
          'recruitment.candidate.detailsPage.funnelStagePath.confirmMessage',
        )}
        submitInProgress={loading}
        onClose={() => setPendingStage(null)}
        onSubmit={handleConfirm}
      />
    </>
  );
};

export default FunnelStagePath;
